import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { Link } from 'react-router-dom';

import FormTypes from 'common/Forms/FormTypes';
import ModalWrapper from 'components/Modals/ModalWrapper';
import { RenderDatePicker } from 'components/Common/Inputs';
import DateTimes from 'components/Common/DateTimes';

const EffectiveDateConfirmationModal = ({
  title,
  isShown,
  onConfirm,
  onCancel,
  error,
  structure,
  defaultEffectiveFrom,
  defaultEffectiveTo,
  versionWindows
}) => {
  const [newEffectiveFromDate, setNewEffectiveFromDate] = useState(defaultEffectiveFrom ?? '');
  const [newEffectiveToDate, setNewEffectiveToDate] = useState(defaultEffectiveTo ?? '');
  const [showInfoMessage, setShowInfoMessage] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const overlappingDateCheck = (newEffectiveFromDateMoment, newEffectiveToDateMoment, currentWindow) => (newEffectiveFromDateMoment <= moment(currentWindow.effectiveTo, 'YYYY-MM-DD') || currentWindow.effectiveTo === null)
      && (newEffectiveToDateMoment >= moment(currentWindow.effectiveFrom, 'YYYY-MM-DD') || newEffectiveToDateMoment === null);

  const disableConfirm = () => isDisabled;

  const formType = FormTypes.getByValue(structure?.basicDetails?.formType)?.code;

  useEffect(() => {
    if (!newEffectiveFromDate || newEffectiveFromDate.trim() === '') {
      setIsDisabled(true);
      setShowInfoMessage(false);
      return;
    }

    if (newEffectiveFromDate !== '') {
      const newEffectiveFromDateMoment = moment(newEffectiveFromDate, 'MM-DD-YYYY');
      const newEffectiveToDateMoment = moment(newEffectiveToDate, 'MM-DD-YYYY');

      if (versionWindows != null) {
        const filteredVersions = versionWindows.filter(version => overlappingDateCheck(newEffectiveFromDateMoment, newEffectiveToDateMoment, version));

        if (!newEffectiveFromDateMoment.isValid()) {
          setIsDisabled(true);
          setShowInfoMessage(false);
          return;
        }

        if (filteredVersions.length >= 2) {
          setShowInfoMessage(true);
          setIsDisabled(true);
          return;
        }
        setIsDisabled(false);
        if (filteredVersions.length === 1) {
          setShowInfoMessage(true);
        }
        if (filteredVersions.length === 0) {
          setShowInfoMessage(false);
        }
      }
    }
  }, [newEffectiveFromDate, newEffectiveToDate]);

  const onConfirmEffectiveDate = () => {
    onConfirm({ effectiveFromDate: newEffectiveFromDate, effectiveToDate: newEffectiveToDate });
  };

  return (
    <ModalWrapper
      modalSize="confirm-effective-date-modal"
      headerClose={ false }
      isOpen={ isShown }
      title={ title }
      btnConfirmTitle="Publish"
      btnConfirmClass="btn-danger"
      btnCancelTitle="Cancel"
      onConfirm={ onConfirmEffectiveDate }
      onCancel={ onCancel }
      disableConfirm={ disableConfirm }
    >
      {showInfoMessage && !isDisabled && <div className="col-12">
        <h2 className="toast toast-blue text-center bold">Previous Statements are effective on these dates. Publishing this Statement will overwrite pre-existing information on the specified days
        </h2>
      </div>}
      {showInfoMessage && isDisabled && <h2 className="toast toast-red text-center bold">Multiple Statements are effective on these dates. Change the Effective Dates of this Statement or&nbsp;
        <Link style={ { textDecoration: 'underline', color: 'white' } } to={ `/forms/${ formType }/${ structure?.basicDetails?.formId }?account=${ structure?.basicDetails?.accountId }` } target="_blank">
          <span>edit</span>
        </Link> the dates of pre-existing statements.
        </h2>}
      <div className="columns">
        <div className="col-6 effective-date-datepicker">
          <p><b>Effective From:</b> (required)</p>
          <RenderDatePicker
            input={ { onChange: e => setNewEffectiveFromDate(e), value: newEffectiveFromDate && newEffectiveFromDate.length > 0 ? newEffectiveFromDate : '' } }
            placeholder="mm-dd-yyyy"
            dateFormat="MM-dd-yyyy"
            maxDate={ newEffectiveToDate && new Date(Date.parse(newEffectiveToDate)) < DateTimes.today ? new Date(Date.parse(newEffectiveToDate)) : DateTimes.today }
            isClearable
          />
        </div>
        <div className="col-6 effective-date-datepicker pl-2">
          <p><b>Effective To:</b> (optional)</p>
          <RenderDatePicker
            input={ { onChange: e => setNewEffectiveToDate(e), value: newEffectiveToDate && newEffectiveToDate.length > 0 ? newEffectiveToDate : '' } }
            placeholder="mm-dd-yyyy"
            dateFormat="MM-dd-yyyy"
            isClearable
            minDate={ newEffectiveFromDate ? new Date(Date.parse(newEffectiveFromDate)) : new Date() }
          />
          <p>Unless a date is set the statement will be published until a new statement provided</p>
        </div>
      </div>
      {
      error
      && <div className="toast toast-error">
        { error }
      </div>
    }
    </ModalWrapper>
  );
};

EffectiveDateConfirmationModal.propTypes = {
  title: PropTypes.string,
  isShown: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  error: PropTypes.string,
  structure: PropTypes.shape(),
  defaultEffectiveFrom: PropTypes.string,
  defaultEffectiveTo: PropTypes.string,
  versionWindows: PropTypes.arrayOf(PropTypes.shape())
};

EffectiveDateConfirmationModal.defaultProps = {
  title: null,
  isShown: false,
  error: null,
  onConfirm: null,
  onCancel: null,
  structure: null,
  defaultEffectiveFrom: null,
  defaultEffectiveTo: null,
  versionWindows: null
};

export default EffectiveDateConfirmationModal;
